// src/components/TimeExpenseManagement.js

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useConfirm } from './ConfirmProvider';
import { formatCurrency } from '../utils/currency';
import { useTimer } from '../contexts/TimerContext';
import { todayInGaborone } from '../utils/dates';

// Helper component for permission denied message
const PermissionDenied = () => (
    <div className="p-4 text-center">
        <div className="max-w-md mx-auto bg-white shadow-md rounded-lg p-6">
            <i className="bi bi-exclamation-triangle-fill text-red-500 text-5xl mb-3 block"></i>
            <h2 className="text-red-600 text-2xl font-bold mb-2">Access Denied</h2>
            <p className="text-gray-600">You do not have the necessary permissions to view this page.</p>
        </div>
    </div>
);


function TimeExpenseManagement({
  cases, clients, timeEntries, expenseEntries, serviceProducts, authAxios,
  fetchTimeEntries, fetchExpenseEntries, fetchInvoices, handleDelete, currentUser,
  expenseCategoryOptions, setTimeEntries, setExpenseEntries, navigateTo, setNavigateTo, userRole,
  showFormMessage
}) {
  // --- RBAC Permissions ---
  const permissions = useMemo(() => ({
    canViewPage: ['PARTNER', 'ASSOCIATE', 'PARALEGAL', 'SUPERUSER'].includes(userRole),
    canCreateEntry: ['PARTNER', 'ASSOCIATE', 'PARALEGAL', 'SUPERUSER'].includes(userRole),
    isPartner: userRole === 'PARTNER' || userRole === 'SUPERUSER',
    isParalegal: userRole === 'PARALEGAL', // Explicitly define for UI checks
  }), [userRole]);
  const confirm = useConfirm();

  const showToast = useCallback((message, type = 'success') => {
    if (typeof showFormMessage === 'function') {
      showFormMessage(type, message);
      return;
    }
    console.warn('Toast:', type, message);
  }, [showFormMessage]);

  // --- Filtered Data based on User Role ---
  const userVisibleCases = useMemo(() => {
      if (permissions.isPartner) {
          return cases || [];
      }
      if (!currentUser || !cases) return [];
      // SENIOR DEV NOTE: Associates and Paralegals should only see cases they are assigned to.
      return cases.filter(c => 
          c.team_members && c.team_members.includes(currentUser.id)
      );
  }, [cases, currentUser, permissions.isPartner]);

  const userVisibleClients = useMemo(() => {
    if (permissions.isPartner) {
        return clients || [];
    }
    if (!clients) return [];
    const visibleClientIds = new Set(userVisibleCases.map(c => c.client));
    return clients.filter(client => visibleClientIds.has(client.id));
  }, [clients, userVisibleCases, permissions.isPartner]);

  const handleDeleteClick = async (item) => {
    if (!item) return;
    const { type, id, fetch, name } = item;
    const endpoint = type === 'time' ? 'time-entries' : 'expense-entries';

    const ok = await confirm({
      title: 'Confirm deletion',
      message: `Are you sure you want to delete this ${name}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      await authAxios.delete(`${endpoint}/${id}/`);
      showToast(`${name} deleted successfully!`, 'success');
      if (fetch) fetch();
    } catch (error) {
      console.error(`Error deleting ${name}:`, error.response ? error.response.data : error.message);
      showToast(`Failed to delete ${name}.`, 'danger');
    }
  };


  // State for UI toggles
  const [showAddTimeEntryForm, setShowAddTimeEntryForm] = useState(false);
  const [showAddExpenseEntryForm, setShowAddExpenseEntryForm] = useState(false);
  const [showEditTimeEntryModal, setShowEditTimeEntryModal] = useState(false);
  const [showEditExpenseEntryModal, setShowEditExpenseEntryModal] = useState(false);
  const [timeEntriesViewMode, setTimeEntriesViewMode] = useState('tiles');
  const [expenseEntriesViewMode, setExpenseEntriesViewMode] = useState('tiles');

  const [showTimeEntryDetailsModal, setShowTimeEntryDetailsModal] = useState(false);
  const [viewingTimeEntry, setViewingTimeEntry] = useState(null);
  const [showExpenseEntryDetailsModal, setShowExpenseEntryDetailsModal] = useState(false);
  const [viewingExpenseEntry, setViewingExpenseEntry] = useState(null);
  
  const [caseSearchTerm, setCaseSearchTerm] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [editCaseSearchTerm, setEditCaseSearchTerm] = useState('');
  const [editClientSearchTerm, setEditClientSearchTerm] = useState('');

  const [addFormFilteredCases, setAddFormFilteredCases] = useState([]);
  const [editFormFilteredCases, setEditFormFilteredCases] = useState([]);


  // --- Timer State (from global context) ---
  const { timerCaseId, isTimerRunning, elapsedTime, startTimer, stopTimer, resetTimer, formatTime } = useTimer();
  const [localTimerCaseId, setLocalTimerCaseId] = useState('');

  // State for new time entry form data
  const [newTimeEntry, setNewTimeEntry] = useState({
    start_time: '', end_time: '', description: '', case: '', client: '', is_billable: true, service_product: ''
  });

  // State for new expense entry form data
  const [newExpenseEntry, setNewExpenseEntry] = useState({
    date: todayInGaborone(), amount: '', category: 'Filing Fees',
    description: '', case: '', client: '', receipt: null, billable: true
  });

  // States for editing
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [editingExpenseEntry, setEditingExpenseEntry] = useState(null);

  const handleStartTimer = () => {
    if (!localTimerCaseId) {
      showToast('Please select a case to start the timer.', 'danger');
      return;
    }
    const selectedCase = userVisibleCases.find(c => c.id.toString() === localTimerCaseId);
    startTimer(localTimerCaseId, selectedCase?.title || '');
  };

  const handleStopTimer = () => {
    const result = stopTimer();
    
    const formatForInput = (date) => {
        const d = new Date(date);
        return d.toISOString().slice(0, 16);
    }

    const selectedCase = userVisibleCases.find(c => c.id.toString() === result.caseId);

    setNewTimeEntry(prev => ({
      ...prev,
      case: result.caseId,
      client: selectedCase ? selectedCase.client : '',
      start_time: formatForInput(result.startTime),
      end_time: formatForInput(result.endTime),
      description: `Time entry for case: ${selectedCase?.title || 'N/A'}`,
      is_billable: true,
      service_product: ''
    }));

    if(selectedCase) {
        setAddFormFilteredCases(userVisibleCases.filter(c => c.client === selectedCase.client));
    }

    setShowAddTimeEntryForm(true);
    setLocalTimerCaseId('');
  };

  const handleResetTimer = () => {
    resetTimer();
    setLocalTimerCaseId('');
  };

  const handleTimeEntryInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setNewTimeEntry(prev => {
        const updated = { ...prev, [name]: val };
        if (name === 'client') {
            if (val) {
                setAddFormFilteredCases(userVisibleCases.filter(c => c.client === parseInt(val)));
            } else {
                setAddFormFilteredCases([]);
            }
            updated.case = '';
        }
        if (name === 'case' && val) {
            const selectedCase = userVisibleCases.find(c => c.id.toString() === val);
            if (selectedCase && !updated.client) {
                updated.client = selectedCase.client;
            }
        }
        return updated;
    });
  };

  const handleTimeEntrySubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!permissions.canCreateEntry) return;

    const toISOStringWithTimezone = (dateTimeLocal) => {
        if (!dateTimeLocal) return null;
        return new Date(dateTimeLocal).toISOString();
    }
    
    const payload = {
      ...newTimeEntry,
      start_time: toISOStringWithTimezone(newTimeEntry.start_time),
      end_time: toISOStringWithTimezone(newTimeEntry.end_time),
      case: newTimeEntry.case || null,
      client: newTimeEntry.client || null,
      service_product: newTimeEntry.service_product || null,
    };
    
    try {
      await authAxios.post('time-entries/', payload);
      showToast('Time Entry added successfully! A draft invoice has been generated.', 'success');
      setShowAddTimeEntryForm(false);
      setNewTimeEntry({ start_time: '', end_time: '', description: '', case: '', client: '', is_billable: true, service_product: '' });
      setAddFormFilteredCases([]);
      fetchTimeEntries();
      fetchInvoices();
    } catch (error) {
      console.error("Frontend: Error adding Time Entry:", error);
      showToast('Failed to add time entry.', 'danger');
    }
  }, [newTimeEntry, authAxios, fetchTimeEntries, fetchInvoices, permissions.canCreateEntry]);

  const openEditTimeEntryModal = useCallback((entry) => {
    const formatForInput = (isoString) => isoString ? isoString.slice(0, 16) : '';
    setEditingTimeEntry({
        ...entry,
        start_time: formatForInput(entry.start_time),
        end_time: formatForInput(entry.end_time),
        service_product: entry.service_product || ''
    });
    if (entry.client) {
        setEditFormFilteredCases(userVisibleCases.filter(c => c.client === entry.client));
    } else {
        setEditFormFilteredCases([]);
    }
    setShowEditTimeEntryModal(true);
  }, [userVisibleCases]);

  const closeEditTimeEntryModal = useCallback(() => {
    setEditingTimeEntry(null);
    setEditFormFilteredCases([]);
    setShowEditTimeEntryModal(false);
  }, []);

  const handleEditingTimeEntryChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setEditingTimeEntry(prev => {
        const updated = { ...prev, [name]: val };
        if (name === 'client') {
            if (val) {
                setEditFormFilteredCases(userVisibleCases.filter(c => c.client === parseInt(val)));
            } else {
                setEditFormFilteredCases([]);
            }
            updated.case = '';
        }
        if (name === 'case' && val) {
            const selectedCase = userVisibleCases.find(c => c.id.toString() === val);
            if (selectedCase && !updated.client) {
                updated.client = selectedCase.client;
            }
        }
        return updated;
    });
  }, [userVisibleCases]);

  const handleEditTimeEntrySubmit = useCallback(async (e) => {
    e.preventDefault();

    const toISOStringWithTimezone = (dateTimeLocal) => {
        if (!dateTimeLocal) return null;
        if (dateTimeLocal.includes('Z')) return dateTimeLocal;
        return new Date(dateTimeLocal).toISOString();
    }

    const payload = {
        ...editingTimeEntry,
        start_time: toISOStringWithTimezone(editingTimeEntry.start_time),
        end_time: toISOStringWithTimezone(editingTimeEntry.end_time),
        case: editingTimeEntry.case || null,
        client: editingTimeEntry.client || null,
        service_product: editingTimeEntry.service_product || null
    };

    try {
      await authAxios.put(`time-entries/${editingTimeEntry.id}/`, payload);
      showToast('Time Entry updated successfully!', 'success');
      closeEditTimeEntryModal();
      fetchTimeEntries();
    } catch (error) {
      console.error("Frontend: Error updating Time Entry:", error);
      showToast('Failed to update time entry.', 'danger');
    }
  }, [editingTimeEntry, authAxios, fetchTimeEntries, closeEditTimeEntryModal]);

  const handleExpenseEntryInputChange = useCallback((e) => {
    const { name, value, files, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    if (name === 'receipt' && files && files[0]) {
      setNewExpenseEntry(prev => ({ ...prev, receipt: files[0] }));
    } else {
      setNewExpenseEntry(prev => {
        const updated = { ...prev, [name]: val };
        if (name === 'client') {
            if (val) {
                setAddFormFilteredCases(userVisibleCases.filter(c => c.client === parseInt(val)));
            } else {
                setAddFormFilteredCases([]);
            }
            updated.case = '';
        }
        if (name === 'case' && val) {
            const selectedCase = userVisibleCases.find(c => c.id.toString() === val);
            if (selectedCase && !updated.client) {
                updated.client = selectedCase.client;
            }
        }
        return updated;
      });
    }
  }, [userVisibleCases]);

  const handleExpenseEntrySubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!permissions.canCreateEntry) return;
    const formData = new FormData();
    Object.keys(newExpenseEntry).forEach(key => {
        if (newExpenseEntry[key] !== null && newExpenseEntry[key] !== '') {
            if (key === 'case' && !newExpenseEntry[key]) {
                formData.append(key, '');
                return;
            }
            if (key === 'client' && !newExpenseEntry[key]) {
                formData.append(key, '');
                return;
            }
            formData.append(key, newExpenseEntry[key]);
        }
    });

    try {
      await authAxios.post('expense-entries/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      showToast('Disbursement added successfully!', 'success');
      setShowAddExpenseEntryForm(false);
      setNewExpenseEntry({
        date: todayInGaborone(), amount: '', category: 'Filing Fees',
        description: '', case: '', client: '', receipt: null, billable: true
      });
      setAddFormFilteredCases([]);
      fetchExpenseEntries();
      fetchInvoices();
    } catch (error) {
      console.error("Frontend: Error adding Disbursement:", error.response ? error.response.data : error);
      showToast('Failed to add disbursement.', 'danger');
    }
  }, [newExpenseEntry, authAxios, fetchExpenseEntries, fetchInvoices, permissions.canCreateEntry]);

  const openEditExpenseEntryModal = useCallback((entry) => {
    setEditingExpenseEntry({ ...entry });
    if (entry.client) {
        setEditFormFilteredCases(userVisibleCases.filter(c => c.client === entry.client));
    } else {
        setEditFormFilteredCases([]);
    }
    setShowEditExpenseEntryModal(true);
  }, [userVisibleCases]);

  const closeEditExpenseEntryModal = useCallback(() => {
    setEditingExpenseEntry(null);
    setEditFormFilteredCases([]);
    setShowEditExpenseEntryModal(false);
  }, []);

  const handleEditingExpenseEntryChange = useCallback((e) => {
    const { name, value, files, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    if (name === 'receipt' && files && files[0]) {
      setEditingExpenseEntry(prev => ({ ...prev, receipt: files[0] }));
    } else {
      setEditingExpenseEntry(prev => {
          const updated = { ...prev, [name]: val };
          if (name === 'client') {
              if (val) {
                  setEditFormFilteredCases(userVisibleCases.filter(c => c.client === parseInt(val)));
              } else {
                  setEditFormFilteredCases([]);
              }
              updated.case = '';
          }
          if (name === 'case' && val) {
              const selectedCase = userVisibleCases.find(c => c.id.toString() === val);
              if (selectedCase && !updated.client) {
                  updated.client = selectedCase.client;
              }
          }
          return updated;
      });
    }
  }, [userVisibleCases]);

  const handleEditExpenseEntrySubmit = useCallback(async (e) => {
    e.preventDefault();
    const formData = new FormData();
    Object.keys(editingExpenseEntry).forEach(key => {
        if (key === 'receipt' && typeof editingExpenseEntry[key] === 'string') {
            return;
        }
        if (editingExpenseEntry[key] !== null && editingExpenseEntry[key] !== '') {
             if (key === 'case' && !editingExpenseEntry[key]) {
                formData.append(key, '');
                return;
            }
            if (key === 'client' && !editingExpenseEntry[key]) {
                formData.append(key, '');
                return;
            }
            formData.append(key, editingExpenseEntry[key]);
        }
    });

    try {
      await authAxios.put(`expense-entries/${editingExpenseEntry.id}/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      showToast('Disbursement updated successfully!', 'success');
      closeEditExpenseEntryModal();
      fetchExpenseEntries();
    } catch (error) {
      console.error("Frontend: Error updating Disbursement:", error);
      showToast('Failed to update disbursement.', 'danger');
    }
  }, [editingExpenseEntry, authAxios, fetchExpenseEntries, closeEditExpenseEntryModal]);

  const openTimeEntryDetailsModal = useCallback((entry) => {
    setViewingTimeEntry(entry);
    setShowTimeEntryDetailsModal(true);
  }, []);
  const closeTimeEntryDetailsModal = useCallback(() => {
    setViewingTimeEntry(null);
    setShowTimeEntryDetailsModal(false);
  }, []);

  const openExpenseEntryDetailsModal = useCallback((entry) => {
    setViewingExpenseEntry(entry);
    setShowExpenseEntryDetailsModal(true);
  }, []);
  const closeExpenseEntryDetailsModal = useCallback(() => {
    setViewingExpenseEntry(null);
    setShowExpenseEntryDetailsModal(false);
  }, []);

  useEffect(() => {
    if (navigateTo && navigateTo.tab === 'timeExpense') {
      if (navigateTo.type === 'time_entry') {
        const timeEntry = timeEntries.find(t => t.id === navigateTo.id);
        if (timeEntry) {
          openTimeEntryDetailsModal(timeEntry);
          setNavigateTo(null);
        }
      } else if (navigateTo.type === 'expense_entry') {
        const expenseEntry = expenseEntries.find(e => e.id === navigateTo.id);
        if (expenseEntry) {
          openExpenseEntryDetailsModal(expenseEntry);
          setNavigateTo(null);
        }
      }
    }
  }, [navigateTo, timeEntries, expenseEntries, openTimeEntryDetailsModal, openExpenseEntryDetailsModal, setNavigateTo]);

  // Open the Log Time form when a Quick Action dispatches 'add_new'.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.tab === 'timeExpense' && e.detail?.type === 'add_new') {
        setShowAddTimeEntryForm(true);
      }
    };
    window.addEventListener('open-resource-detail', handler);
    return () => window.removeEventListener('open-resource-detail', handler);
  }, []);

  const getClientDisplayName = (client) => client.organization_name || `${client.first_name} ${client.last_name}`;

  const canPerformAction = (entry) => {
      if (entry.billing_status === 'BILLED') return false;
      if (permissions.isPartner) return true;
      return entry.created_by === currentUser.user_id;
  };
  
  if (!permissions.canViewPage) {
    return <PermissionDenied />;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Local toast UI removed in favor of global toasts (showFormMessage). */}
      <div className="flex justify-between items-center mb-6">
          <div>
              <h2 className="text-3xl font-bold text-gray-800">Time & Expense Management</h2>
              {permissions.canCreateEntry && (
                  <div className="flex space-x-4 mt-4">
                      <button 
                          onClick={() => setShowAddTimeEntryForm(true)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                      >
                           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.414-1.414L11 10.586V6z" clipRule="evenodd" />
                           </svg>
                           Add Time Entry
                      </button>
                      <button 
                          onClick={() => setShowAddExpenseEntryForm(true)}
                          className="bg-teal-500 text-white px-4 py-2 rounded-md shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 flex items-center"
                      >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4z" />
                            <path fillRule="evenodd" d="M3 10h14v5a1 1 0 01-1 1H4a1 1 0 01-1-1v-5zm2 2a1 1 0 011-1h2a1 1 0 110 2H6a1 1 0 01-1-1zm5 0a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                          Add Disbursement
                      </button>
                  </div>
              )}
          </div>
      </div>

      {permissions.canCreateEntry && (
          <div className="bg-white shadow-md rounded-lg mb-8">
              <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
                  <h5 className="text-lg font-bold text-gray-800">Live Time Tracker</h5>
              </div>
              <div className="p-6 flex flex-col md:flex-row items-center gap-4">
                  <div className="w-full md:w-1/3">
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={isTimerRunning ? timerCaseId : localTimerCaseId} onChange={(e) => setLocalTimerCaseId(e.target.value)} disabled={isTimerRunning}>
                          <option value="">Select a Case...</option>
                          {userVisibleCases.map(_case => (<option key={_case.id} value={_case.id}>{_case.title}</option>))}
                      </select>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg text-center my-3 md:my-0 w-full md:w-1/3">
                      <h3 className="text-3xl font-mono">{formatTime(elapsedTime)}</h3>
                  </div>
                  <div className="flex justify-around space-x-2 w-full md:w-1/3">
                      {!isTimerRunning ? (
                          <button className="bg-green-500 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex-grow flex items-center justify-center" onClick={handleStartTimer} disabled={!localTimerCaseId}>
                              <i className="bi bi-play-circle mr-1"></i>Start
                          </button>
                      ) : (
                          <button className="bg-yellow-500 text-white px-4 py-2 rounded-md shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 flex-grow flex items-center justify-center" onClick={handleStopTimer}>
                              <i className="bi bi-stop-circle mr-1"></i>Stop & Log
                          </button>
                      )}
                      <button className="bg-red-500 text-white px-4 py-2 rounded-md shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex-grow flex items-center justify-center" onClick={handleResetTimer} disabled={!isTimerRunning && elapsedTime === 0}>
                          <i className="bi bi-arrow-counterclockwise mr-1"></i>Reset
                      </button>
                  </div>
              </div>
          </div>
      )}

      {showAddTimeEntryForm && (
          <div className="fixed inset-0 overflow-hidden z-50">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAddTimeEntryForm(false)}></div>
              <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                <div className="w-screen max-w-md">
                  <div className="h-full flex flex-col bg-white shadow-xl">
                    <div className="flex-shrink-0 px-4 py-4 flex items-center justify-between border-b">
                      <h2 className="text-lg font-medium text-gray-900">New Time Entry</h2>
                      <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={() => setShowAddTimeEntryForm(false)}>
                        <span className="sr-only">Close panel</span>
                        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        <form onSubmit={handleTimeEntrySubmit} className="h-full flex flex-col">
                            <div className="flex-1 p-6 space-y-6">
                                <div>
                                    <label htmlFor="start_time" className="block text-sm font-medium text-gray-700">Start Time:</label>
                                    <input type="datetime-local" id="start_time" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="start_time" value={newTimeEntry.start_time} onChange={handleTimeEntryInputChange} required />
                                </div>
                                <div>
                                    <label htmlFor="end_time" className="block text-sm font-medium text-gray-700">End Time:</label>
                                    <input type="datetime-local" id="end_time" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="end_time" value={newTimeEntry.end_time} onChange={handleTimeEntryInputChange} required />
                                </div>
                                <div>
                                    <label htmlFor="client" className="block text-sm font-medium text-gray-700">Client:</label>
                                    <select id="client" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="client" value={newTimeEntry.client} onChange={handleTimeEntryInputChange}>
                                        <option value="">Select Client</option>
                                        {userVisibleClients.map(client => (<option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="case" className="block text-sm font-medium text-gray-700">Case:</label>
                                    <select id="case" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="case" value={newTimeEntry.case} onChange={handleTimeEntryInputChange}>
                                        <option value="">Select Case</option>
                                        {addFormFilteredCases.map(_case => (<option key={_case.id} value={_case.id}>{_case.title}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="service_product" className="block text-sm font-medium text-gray-700">Service/Product (for billing):</label>
                                    <select id="service_product" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="service_product" value={newTimeEntry.service_product} onChange={handleTimeEntryInputChange}>
                                        <option value="">Select a service...</option>
                                        {serviceProducts.filter(sp => sp.is_active).map(sp => (
                                            <option key={sp.id} value={sp.id}>
                                                {sp.name}{!permissions.isParalegal && ` - P${parseFloat(sp.price).toFixed(2)}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description:</label>
                                    <textarea id="description" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="description" value={newTimeEntry.description} onChange={handleTimeEntryInputChange} required></textarea>
                                </div>
                                <div className="flex items-center">
                                    <input id="isBillableCheck" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" name="is_billable" checked={newTimeEntry.is_billable} onChange={handleTimeEntryInputChange} />
                                    <label htmlFor="isBillableCheck" className="ml-2 block text-sm text-gray-900">Billable</label>
                                </div>
                            </div>
                            <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t">
                                <button type="submit" className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Add Time Entry</button>
                            </div>
                        </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      )}
      {showAddExpenseEntryForm && (
          <div className="fixed inset-0 overflow-hidden z-50">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAddExpenseEntryForm(false)}></div>
              <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                <div className="w-screen max-w-md">
                  <div className="h-full flex flex-col bg-white shadow-xl">
                     <div className="flex-shrink-0 px-4 py-4 flex items-center justify-between border-b">
                        <h2 className="text-lg font-medium text-gray-900">New Disbursement</h2>
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={() => setShowAddExpenseEntryForm(false)}>
                          <span className="sr-only">Close panel</span>
                          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    <div className="flex-1 overflow-y-auto">
                        <form onSubmit={handleExpenseEntrySubmit} className="h-full flex flex-col">
                           <div className="flex-1 p-6 space-y-6">
                                <div>
                                    <label htmlFor="date" className="block text-sm font-medium text-gray-700">Date:</label>
                                    <input type="date" id="date" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="date" value={newExpenseEntry.date} onChange={handleExpenseEntryInputChange} required />
                                </div>
                                <div>
                                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700">{newExpenseEntry.category === 'Serving' ? 'Rate (per km or per unit):' : 'Amount:'}</label>
                                    <input type="number" step="0.01" id="amount" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="amount" value={newExpenseEntry.amount} onChange={handleExpenseEntryInputChange} required />
                                </div>
                                <div>
                                    <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category:</label>
                                    <select id="category" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="category" value={newExpenseEntry.category} onChange={handleExpenseEntryInputChange}>
                                        {(expenseCategoryOptions || []).map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="receipt" className="block text-sm font-medium text-gray-700">Receipt (Optional):</label>
                                    <input type="file" id="receipt" className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" name="receipt" onChange={handleExpenseEntryInputChange} />
                                </div>
                                <div>
                                    <label htmlFor="client_expense" className="block text-sm font-medium text-gray-700">Client:</label>
                                    <select id="client_expense" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="client" value={newExpenseEntry.client} onChange={handleExpenseEntryInputChange} required>
                                        <option value="">Select Client</option>
                                        {userVisibleClients.map(client => (<option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="case_expense" className="block text-sm font-medium text-gray-700">Case:</label>
                                    <select id="case_expense" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="case" value={newExpenseEntry.case} onChange={handleExpenseEntryInputChange} required>
                                        <option value="">Select Case</option>
                                        {addFormFilteredCases.map(_case => (<option key={_case.id} value={_case.id}>{_case.title}</option>))}
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label htmlFor="description_expense" className="block text-sm font-medium text-gray-700">Description:</label>
                                    <textarea id="description_expense" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="description" value={newExpenseEntry.description} onChange={handleExpenseEntryInputChange}></textarea>
                                </div>
                                <div className="md:col-span-2 flex items-center">
                                    <input id="isBillableCheckExpense" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" name="billable" checked={newExpenseEntry.billable} onChange={handleExpenseEntryInputChange} />
                                    <label htmlFor="isBillableCheckExpense" className="ml-2 block text-sm text-gray-900">Billable</label>
                                </div>
                            </div>
                            <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t">
                                <button type="submit" className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Add Disbursement</button>
                            </div>
                        </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      )}
      
      {/* Time Entries Section */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 bg-gray-100 border-b border-gray-200 flex justify-between items-center">
              <h5 className="text-lg font-bold text-gray-800">All Time Entries</h5>
              <div className="flex space-x-2">
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${timeEntriesViewMode === 'list' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setTimeEntriesViewMode('list')}>List</button>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${timeEntriesViewMode === 'tiles' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setTimeEntriesViewMode('tiles')}>Tile</button>
              </div>
          </div>
          <div className="p-6">
              {timeEntriesViewMode === 'list' ? (
                  <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Time</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration (Hrs)</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case / Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                              {timeEntries.map(entry => {
                                const rate = Number(entry.billing_rate) || 0;
                                const hours = Number(entry.hours || entry.duration_hours) || 0;
                                const amount = rate * hours;
                                const statusColors = { UNBILLED: 'bg-yellow-100 text-yellow-800', BILLED: 'bg-green-100 text-green-800', WRITTEN_OFF: 'bg-red-100 text-red-800', NON_BILLABLE: 'bg-gray-100 text-gray-600' };
                                const billingStatus = entry.billing_status || (entry.is_billable ? 'UNBILLED' : 'NON_BILLABLE');
                                return (<tr key={entry.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(entry.start_time).toLocaleString()}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{hours}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rate ? formatCurrency(rate) : '—'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">{amount ? formatCurrency(amount) : '—'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{entry.description}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{entry.case_title || entry.client_full_name || 'N/A'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{entry.service_product_name || 'N/A'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[billingStatus] || 'bg-gray-100 text-gray-600'}`}>{billingStatus.replace('_', ' ')}</span></td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center space-x-4">
                                      {canPerformAction(entry) && (
                                        <button className="text-blue-600 hover:text-blue-900 flex items-center" onClick={() => openEditTimeEntryModal(entry)} title="Edit Entry">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>Edit
                                        </button>
                                      )}
                                      <button className="text-gray-600 hover:text-gray-900 flex items-center" onClick={() => openTimeEntryDetailsModal(entry)} title="View Details">
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>View
                                      </button>
                                      {canPerformAction(entry) && (
                                          <button className="text-red-600 hover:text-red-900 flex items-center" onClick={() => handleDeleteClick({ type: 'time', id: entry.id, fetch: fetchTimeEntries, name: 'Time Entry' })} title="Delete Entry">
                                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>Delete
                                          </button>
                                      )}
                                  </td></tr>); })}
                          </tbody>
                      </table>
                      {timeEntries.length === 0 && <p className="text-center py-4 text-sm text-gray-500">No time entries found.</p>}
                  </div>
              ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {timeEntries.map(entry => (
                          <div key={entry.id} className="bg-white shadow-md rounded-lg overflow-hidden flex flex-col">
                              <div className="p-6 flex-grow">
                                  <h5 className="text-lg font-bold text-gray-800">{entry.case_title || entry.client_full_name}</h5>
                                  <p className="text-sm text-gray-600 mt-2 flex-grow">{entry.description}</p>
                                  <p className="text-sm text-gray-500 mt-4"><small>Service: {entry.service_product_name || 'N/A'}</small></p>
                                  <p className="text-sm text-gray-500"><small>Duration: {entry.duration_hours} hrs</small></p>
                                  <p className="text-sm text-gray-500 mt-3">{entry.is_billable ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Billable</span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Non-Billable</span>}</p>
                                </div>
                                <div className="p-6 pt-0 mt-auto flex justify-end flex-wrap gap-2">
                                    {canPerformAction(entry) && (
                                        <button className="text-blue-600 hover:text-blue-900 text-sm flex items-center" onClick={() => openEditTimeEntryModal(entry)} title="Edit Entry">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>Edit
                                        </button>
                                    )}
                                    <button className="text-gray-600 hover:text-gray-900 text-sm flex items-center" onClick={() => openTimeEntryDetailsModal(entry)} title="View Details">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>View
                                    </button>
                                    {canPerformAction(entry) && (
                                        <button className="text-red-600 hover:text-red-900 text-sm flex items-center" onClick={() => handleDeleteClick({ type: 'time', id: entry.id, fetch: fetchTimeEntries, name: 'Time Entry' })} title="Delete Entry">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>Delete
                                        </button>
                                    )}
                              </div>
                          </div>
                      ))}
                      {timeEntries.length === 0 && <div className="col-span-full"><p className="text-center py-4 text-sm text-gray-500">No time entries found.</p></div>}
                  </div>
              )}
          </div>
      </div>


      {/* Disbursements Section */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-100 border-b border-gray-200 flex justify-between items-center">
              <h5 className="text-lg font-bold text-gray-800">All Disbursements</h5>
              <div className="flex space-x-2">
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${expenseEntriesViewMode === 'list' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setExpenseEntriesViewMode('list')}>List</button>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${expenseEntriesViewMode === 'tiles' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setExpenseEntriesViewMode('tiles')}>Tile</button>
              </div>
          </div>
          <div className="p-6">
              {expenseEntriesViewMode === 'list' ? (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case / Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {expenseEntries.map(entry => {
                                const expStatusColors = { UNBILLED: 'bg-yellow-100 text-yellow-800', BILLED: 'bg-green-100 text-green-800', WRITTEN_OFF: 'bg-red-100 text-red-800' };
                                const expBillingStatus = entry.billing_status || (entry.billable ? 'UNBILLED' : 'NON_BILLABLE');
                                return (<tr key={entry.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.date}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(entry.amount)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{entry.category}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{entry.case_title || entry.client_full_name || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${expStatusColors[expBillingStatus] || 'bg-gray-100 text-gray-600'}`}>{(expBillingStatus || '').replace('_', ' ')}</span>{expBillingStatus === 'BILLED' && <span className="ml-1 text-gray-400" title="Locked — already billed">🔒</span>}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{entry.receipt ? (<a href={entry.receipt} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">View</a>) : 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center space-x-4">
                                    {canPerformAction(entry) && (
                                        <button className="text-blue-600 hover:text-blue-900 flex items-center" onClick={() => openEditExpenseEntryModal(entry)} title="Edit Entry">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>Edit
                                        </button>
                                    )}
                                    <button className="text-gray-600 hover:text-gray-900 flex items-center" onClick={() => openExpenseEntryDetailsModal(entry)} title="View Details">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>View
                                    </button>
                                    {canPerformAction(entry) && (
                                        <button className="text-red-600 hover:text-red-900 flex items-center" onClick={() => handleDeleteClick({ type: 'expense', id: entry.id, fetch: fetchExpenseEntries, name: 'Disbursement' })} title="Delete Entry">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>Delete
                                        </button>
                                    )}
                                </td></tr>); })}
                        </tbody>
                    </table>
                    {expenseEntries.length === 0 && <p className="text-center py-4 text-sm text-gray-500">No disbursements found.</p>}
                </div>
              ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {expenseEntries.map(entry => (
                          <div key={entry.id} className="bg-white shadow-md rounded-lg overflow-hidden flex flex-col">
                              <div className="p-6 flex-grow">
                                    <h5 className="text-lg font-bold text-gray-800">P{entry.amount}</h5>
                                    <p className="text-sm text-gray-600 mt-2"><small>Case: {entry.case_title || entry.client_full_name}</small></p>
                                    <p className="text-sm text-gray-600"><small>Category: {entry.category}</small></p>
                                    <p className="text-sm text-gray-600 mt-3">{entry.billable ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Billable</span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Non-Billable</span>}</p>
                                </div>
                                <div className="p-6 pt-0 mt-auto flex justify-end flex-wrap gap-2">
                                    {canPerformAction(entry) && (
                                        <button className="text-blue-600 hover:text-blue-900 text-sm flex items-center" onClick={() => openEditExpenseEntryModal(entry)} title="Edit Entry">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>Edit
                                        </button>
                                    )}
                                    <button className="text-gray-600 hover:text-gray-900 text-sm flex items-center" onClick={() => openExpenseEntryDetailsModal(entry)} title="View Details">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>View
                                    </button>
                                    {canPerformAction(entry) && (
                                        <button className="text-red-600 hover:text-red-900 text-sm flex items-center" onClick={() => handleDeleteClick({ type: 'expense', id: entry.id, fetch: fetchExpenseEntries, name: 'Disbursement' })} title="Delete Entry">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>Delete
                                        </button>
                                    )}
                                </div>
                          </div>
                      ))}
                      {expenseEntries.length === 0 && <div className="col-span-full"><p className="text-center py-4 text-sm text-gray-500">No disbursements found.</p></div>}
                  </div>
              )}
          </div>
      </div>


      {/* --- MODIFIED: Edit Time Entry Side Panel --- */}
      {showEditTimeEntryModal && editingTimeEntry && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeEditTimeEntryModal}></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
              <div className="w-screen max-w-md">
                <div className="h-full flex flex-col py-6 bg-white shadow-xl">
                  <div className="px-4 sm:px-6">
                    <div className="flex items-start justify-between">
                      <h2 className="text-lg font-medium text-gray-900">Edit Time Entry</h2>
                      <div className="ml-3 h-7 flex items-center">
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={closeEditTimeEntryModal}>
                          <span className="sr-only">Close panel</span>
                          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 relative flex-1 px-4 sm:px-6 overflow-y-auto">
                    <form onSubmit={handleEditTimeEntrySubmit} className="space-y-6 pb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Start Time:</label>
                        <input type="datetime-local" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="start_time" value={editingTimeEntry.start_time} onChange={handleEditingTimeEntryChange} required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">End Time:</label>
                        <input type="datetime-local" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="end_time" value={editingTimeEntry.end_time} onChange={handleEditingTimeEntryChange} required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Client:</label>
                        <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="client" value={editingTimeEntry.client || ''} onChange={handleEditingTimeEntryChange}>
                          <option value="">Select Client</option>
                          {userVisibleClients.map(client => (<option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Case:</label>
                        <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="case" value={editingTimeEntry.case || ''} onChange={handleEditingTimeEntryChange}>
                          <option value="">Select Case</option>
                          {editFormFilteredCases.map(_case => (<option key={_case.id} value={_case.id}>{_case.title}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Service/Product:</label>
                        <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="service_product" value={editingTimeEntry.service_product} onChange={handleEditingTimeEntryChange}>
                            <option value="">Select a service...</option>
                            {serviceProducts.filter(sp => sp.is_active).map(sp => (
                                <option key={sp.id} value={sp.id}>
                                    {sp.name}{!permissions.isParalegal && ` - ${formatCurrency(sp.price)}`}
                                </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description:</label>
                        <textarea className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="description" value={editingTimeEntry.description || ''} onChange={handleEditingTimeEntryChange}></textarea>
                      </div>
                      <div className="flex items-center">
                        <input className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" type="checkbox" name="is_billable" checked={editingTimeEntry.is_billable} onChange={handleEditingTimeEntryChange} id="isBillableCheckEdit"/>
                        <label className="ml-2 block text-sm text-gray-900" htmlFor="isBillableCheckEdit">Billable</label>
                      </div>
                      <div className="flex justify-end space-x-4 pt-4 border-t">
                        <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" onClick={closeEditTimeEntryModal}>Cancel</button>
                        <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">Save Changes</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
      )}
      
      {/* --- MODIFIED: Edit Disbursement Side Panel --- */}
      {showEditExpenseEntryModal && editingExpenseEntry && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeEditExpenseEntryModal}></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
              <div className="w-screen max-w-md">
                <div className="h-full flex flex-col py-6 bg-white shadow-xl">
                  <div className="px-4 sm:px-6">
                    <div className="flex items-start justify-between">
                      <h2 className="text-lg font-medium text-gray-900">Edit Disbursement</h2>
                      <div className="ml-3 h-7 flex items-center">
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={closeEditExpenseEntryModal}>
                          <span className="sr-only">Close panel</span>
                           <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 relative flex-1 px-4 sm:px-6 overflow-y-auto">
                    <form onSubmit={handleEditExpenseEntrySubmit} className="space-y-6 pb-6">
                      <div>
                          <label className="block text-sm font-medium text-gray-700">Date:</label>
                          <input type="date" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="date" value={editingExpenseEntry.date || ''} onChange={handleEditingExpenseEntryChange} required />
                      </div>
                      <div>
                          <label className="block text-sm font-medium text-gray-700">{editingExpenseEntry.category === 'Serving' ? 'Rate (per km or per unit):' : 'Amount:'}</label>
                          <input type="number" step="0.01" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="amount" value={editingExpenseEntry.amount || ''} onChange={handleEditingExpenseEntryChange} required />
                      </div>
                      <div>
                          <label className="block text-sm font-medium text-gray-700">Category:</label>
                          <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="category" value={editingExpenseEntry.category || ''} onChange={handleEditingExpenseEntryChange}>
                            {(expenseCategoryOptions || []).map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
                          </select>
                      </div>
                      <div>
                          <label className="block text-sm font-medium text-gray-700">Replace Receipt:</label>
                          <input type="file" className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" name="receipt" onChange={handleEditingExpenseEntryChange} />
                          {editingExpenseEntry.receipt && typeof editingExpenseEntry.receipt === 'string' && (<small>Current: <a href={editingExpenseEntry.receipt} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">View</a></small>)}
                      </div>
                      <div>
                          <label className="block text-sm font-medium text-gray-700">Client:</label>
                          <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="client" value={editingExpenseEntry.client || ''} onChange={handleEditingExpenseEntryChange} required>
                            <option value="">Select Client</option>
                            {userVisibleClients.map(client => (<option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>))}
                          </select>
                      </div>
                      <div>
                          <label className="block text-sm font-medium text-gray-700">Case:</label>
                          <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="case" value={editingExpenseEntry.case || ''} onChange={handleEditingExpenseEntryChange} required>
                            <option value="">Select Case</option>
                            {editFormFilteredCases.map(_case => (<option key={_case.id} value={_case.id}>{_case.title}</option>))}
                          </select>
                      </div>
                      <div>
                          <label className="block text-sm font-medium text-gray-700">Description:</label>
                          <textarea className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="description" value={editingExpenseEntry.description || ''} onChange={handleEditingExpenseEntryChange}></textarea>
                      </div>
                      <div className="flex items-center">
                          <input className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" type="checkbox" name="billable" checked={editingExpenseEntry.billable} onChange={handleEditingExpenseEntryChange} id="isBillableCheckEditExpense" />
                          <label className="ml-2 block text-sm text-gray-900" htmlFor="isBillableCheckEditExpense">Billable</label>
                      </div>
                      <div className="flex justify-end space-x-4 pt-4 border-t">
                          <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" onClick={closeEditExpenseEntryModal}>Cancel</button>
                          <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">Save Changes</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
      )}
      
      {showTimeEntryDetailsModal && viewingTimeEntry && (
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showTimeEntryDetailsModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className={`absolute top-0 right-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col ${showTimeEntryDetailsModal ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10 flex-shrink-0">
                    <h5 className="text-2xl font-bold text-gray-800">Time Entry Details</h5>
                    <button onClick={closeTimeEntryDetailsModal} className="text-gray-600 hover:text-gray-900"><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="p-6 space-y-4 overflow-y-auto flex-grow">
                    <ul className="divide-y divide-gray-200">
                        <li className="py-2"><strong>Start Time:</strong> {new Date(viewingTimeEntry.start_time).toLocaleString()}</li>
                        <li className="py-2"><strong>End Time:</strong> {new Date(viewingTimeEntry.end_time).toLocaleString()}</li>
                        <li className="py-2"><strong>Duration:</strong> {viewingTimeEntry.duration_hours} hours</li>
                        <li className="py-2"><strong>Description:</strong> {viewingTimeEntry.description}</li>
                        <li className="py-2"><strong>Case:</strong> {viewingTimeEntry.case_title || 'N/A'}</li>
                        <li className="py-2"><strong>Client:</strong> {viewingTimeEntry.client_full_name || 'N/A'}</li>
                        <li className="py-2"><strong>Service:</strong> {viewingTimeEntry.service_product_name || 'N/A'}</li>
                        <li className="py-2"><strong>Billable:</strong> {viewingTimeEntry.is_billable ? 'Yes' : 'No'}</li>
                    </ul>
                </div>
                <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t">
                    <button type="button" onClick={closeTimeEntryDetailsModal} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Close</button>
                </div>
            </div>
        </div>
      )}

      {showExpenseEntryDetailsModal && viewingExpenseEntry && (
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showExpenseEntryDetailsModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className={`absolute top-0 right-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col ${showExpenseEntryDetailsModal ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10 flex-shrink-0">
                    <h5 className="text-2xl font-bold text-gray-800">Disbursement Details</h5>
                    <button onClick={closeExpenseEntryDetailsModal} className="text-gray-600 hover:text-gray-900"><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="p-6 space-y-4 overflow-y-auto flex-grow">
                    <ul className="divide-y divide-gray-200">
                        <li className="py-2"><strong>Date:</strong> {viewingExpenseEntry.date}</li>
                        <li className="py-2"><strong>Amount:</strong> P{viewingExpenseEntry.amount}</li>
                        <li className="py-2"><strong>Category:</strong> {viewingExpenseEntry.category}</li>
                        <li className="py-2"><strong>Description:</strong> {viewingExpenseEntry.description || 'N/A'}</li>
                        <li className="py-2"><strong>Case:</strong> {viewingExpenseEntry.case_title || 'N/A'}</li>
                        <li className="py-2"><strong>Client:</strong> {viewingExpenseEntry.client_full_name || 'N/A'}</li>
                        <li className="py-2"><strong>Billable:</strong> {viewingExpenseEntry.billable ? 'Yes' : 'No'}</li>
                        <li className="py-2"><strong>Receipt:</strong> {viewingExpenseEntry.receipt ? <a href={viewingExpenseEntry.receipt} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">View Receipt</a> : 'N/A'}</li>
                    </ul>
                </div>
                 <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t">
                    <button type="button" onClick={closeExpenseEntryDetailsModal} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Close</button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
}

export default TimeExpenseManagement;
